// Who is signed in, from the API rather than from a token in the browser.
//
// §05: the Keycloak tokens live server-side and the browser holds an opaque,
// revocable cookie it cannot read. So the provider does not decode anything —
// it asks `GET /api/auth/session` and maps the three outcomes of that ONE query
// onto the three `AuthState` variants:
//
//     pending  -> loading       (we genuinely do not know yet)
//     200      -> authenticated (the API resolved a principal from the cookie)
//     401      -> anonymous     (a normal answer from a public endpoint)
//
// Nothing in this app reads a cookie, a token, or `localStorage` — it cannot,
// by design — which is why role checks (`useHasRole`) route through the
// principal the API reported rather than through anything the client decided.
//
// The listener registered below closes the other half of the loop: a session
// that expires mid-visit is invisible until a request fails, so ONE 401 from
// any API call invalidates this query and drops the UI to the signed-out
// screen, instead of leaving every panel to fail separately with "unauthorized".

import { useQueryClient } from "@tanstack/react-query";
import { createContext, useEffect, useMemo, type ReactNode } from "react";
import { setUnauthorizedListener } from "../api/client.js";
import { describeError } from "../api/problem.js";
import type { AuthState } from "./principal.js";
import { currentPath, loginUrl, SESSION_QUERY_KEY, useSessionQuery } from "./session.js";

export const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Tests (and Storybook-style harnesses) inject a principal here. */
  value?: AuthState;
}

export function AuthProvider({ children, value }: AuthProviderProps): ReactNode {
  const queryClient = useQueryClient();
  const session = useSessionQuery(value === undefined);

  useEffect(() => {
    if (value !== undefined) return;
    setUnauthorizedListener(() => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    });
    return () => setUnauthorizedListener(null);
  }, [queryClient, value]);

  const state = useMemo<AuthState>(() => {
    if (value !== undefined) return value;
    if (session.isPending) return { status: "loading" };

    if (session.error !== null) {
      // The session endpoint answering with something other than 200 or 401 —
      // a 502 from the ingress, the API down. Treated as signed-out because
      // that is the only safe reading, but the reason is shown rather than
      // replaced with a generic "please sign in".
      const described = describeError(session.error);
      return {
        status: "anonymous",
        cause: "unreachable",
        reason: described.detail ?? described.title,
        signInUrl: loginUrl(currentPath()),
      };
    }

    if (session.data === null || session.data === undefined) {
      return {
        status: "anonymous",
        cause: "unauthenticated",
        reason: "You are not signed in.",
        signInUrl: loginUrl(currentPath()),
      };
    }

    return { status: "authenticated", principal: session.data };
  }, [value, session.isPending, session.error, session.data]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
